import React, { useRef, useState, useEffect } from 'react';
import { Col, Row } from 'antd';
import { Field, FieldProps } from 'formik';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import DeleteOutlinedIcon from 'assets/delete_outlined.svg?react';
import AlertCircleIcon from 'assets/alert-circle.svg?react';
import { mapToPickerColor, mapToStorageColor } from 'constants/colors';
import TagInputField from '../TagInputField';
import TagColorPicker from '../TagColorPicker';
import { TAG_MANAGER_STRINGS } from '../../constants';

interface TagColorRowProps {
  tag: {
    id: string;
    name: string;
    backgroundColor: string;
    textColor: string;
    color: string;
  };
  onColorChange: (tagId: string, color: string) => void;
  onDelete?: (tagId: string) => void;
  onCreateNewTag?: () => void;
  totalTagsCount: number;
  formValues: any;
  isNewTag?: boolean;
  originalName?: string;
}

const TagColorRow: React.FC<TagColorRowProps> = ({
  tag,
  onColorChange,
  onDelete,
  onCreateNewTag,
  totalTagsCount,
  formValues,
  isNewTag = false,
  originalName = ''
}) => {
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = () => {
    if (onDelete) {
      // Skip confirmation modal for new tags that haven't been saved yet
      if (isNewTag) {
        onDelete(tag.id);
      } else {
        setShowDeleteModal(true);
      }
    }
  };

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete(tag.id);
    }
    setShowDeleteModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Close modal when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking on the modal itself or the delete button
      if (
        modalRef.current?.contains(target) ||
        deleteButtonRef.current?.contains(target)
      ) {
        return;
      }

      // Close modal for any other click
      setShowDeleteModal(false);
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDeleteModal(false);
      }
    };

    if (showDeleteModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showDeleteModal]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === TAG_MANAGER_STRINGS.ENTER &&
      onCreateNewTag &&
      totalTagsCount >= 1
    ) {
      e.preventDefault();
      onCreateNewTag();
    }
  };

  const fieldName = `${TAG_MANAGER_STRINGS.TAGS_FIELD}.${tag.id}.name`;

  return (
    <div style={{ marginBottom: '10px' }}>
      <Row
        className="items-center"
        align="middle"
        gutter={8}
        wrap={false}
        style={{ minHeight: 40 }}
      >
        <Col style={{ flexShrink: 0 }}>
          <TagInputField
            tag={tag}
            fieldName={fieldName}
            formValues={formValues}
            onKeyDown={handleKeyDown}
            isNewTag={isNewTag}
            originalName={originalName}
          />
        </Col>
        <Col style={{ flexShrink: 0 }}>
          <Field name={`${TAG_MANAGER_STRINGS.TAGS_FIELD}.${tag.id}.color`}>
            {({ field }: FieldProps) => (
              <TagColorPicker
                tagId={tag.id}
                value={mapToPickerColor(field.value)} // Convert storage color to picker color
                onChange={(tagId: string, pickerColor: string) => {
                  // Convert picker color back to storage color before saving
                  const storageColor = mapToStorageColor(pickerColor);
                  onColorChange(tagId, storageColor);
                }}
              />
            )}
          </Field>
        </Col>
        <Col style={{ flexShrink: 0, position: 'relative' }}>
          <button
            ref={deleteButtonRef}
            type="button"
            onClick={handleDelete}
            className="flex items-center justify-center cursor-pointer rounded hover:bg-gray-100 border-0 bg-transparent"
            style={{
              minHeight: 40,
              width: 40
            }}
            aria-label={TAG_MANAGER_STRINGS.DELETE_TAG}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (
                e.key === TAG_MANAGER_STRINGS.ENTER ||
                e.key === TAG_MANAGER_STRINGS.SPACE
              ) {
                e.preventDefault();
                handleDelete();
              }
            }}
          >
            <DeleteOutlinedIcon width={20} height={20} />
          </button>

          {/* Simple positioned modal */}
          {showDeleteModal && (
            <div
              ref={modalRef}
              className="absolute bg-white border border-gray-300 rounded shadow-lg z-50"
              aria-modal="true"
              aria-labelledby="delete-modal-title"
              aria-describedby="delete-modal-description"
              style={{
                top: '100%',
                right: '0',
                marginTop: '1px',
                width: '340px',
                height: '176px',
                padding: '16px',
                boxSizing: 'border-box'
              }}
            >
              <div className="flex items-center mb-1" style={{ gap: '13px' }}>
                <AlertCircleIcon width={24} height={24} />
                <span
                  id="delete-modal-title"
                  className="font-semibold text-gray-900 text-base"
                >
                  {TAG_MANAGER_STRINGS.DELETE_TAG_TITLE}
                </span>
              </div>

              <div
                id="delete-modal-description"
                className="text-sm text-gray-900 mb-4"
                style={{
                  marginLeft: '38px',
                  width: '268px'
                }}
              >
                {TAG_MANAGER_STRINGS.DELETE_TAG_DESCRIPTION}
              </div>

              <div className="flex justify-end w-full" style={{ gap: '8px' }}>
                <Button
                  type={ButtonType.outline}
                  onClick={handleCancelDelete}
                  className="w-19 h-10"
                >
                  {TAG_MANAGER_STRINGS.DELETE_TAG_CANCEL}
                </Button>
                <Button
                  type={ButtonType.danger}
                  onClick={handleConfirmDelete}
                  className="w-19 h-10"
                >
                  {TAG_MANAGER_STRINGS.DELETE_TAG_BUTTON}
                </Button>
              </div>
            </div>
          )}
        </Col>
      </Row>
      {/* Error message displayed outside the row */}
      <Field name={fieldName}>
        {({ meta }: FieldProps) => {
          const hasError = meta.touched && meta.error;
          return hasError ? (
            <div
              className="text-red-500 text-xs ml-0"
              style={{ marginTop: '2px', marginBottom: '8px' }}
            >
              {meta.error}
            </div>
          ) : null;
        }}
      </Field>
    </div>
  );
};

export default TagColorRow;
